import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Button, Card, Cluster, Input, Stack, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { useAccess } from "../../shell/AccessProvider";
import { opsApi, type SupportComment, type SupportTicket } from "../../lib/opsApi";
import { ACTION_HINTS, FIELD_HINTS, TICKET_STATUS_OPTIONS } from "../../help/fieldHints";
import type { LeadInput } from "../../lib/crmApi";
import { DetailSection, FieldItem, RecordWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { StatusBadge } from "./StatusBadge";
import { LeadCaptureForm } from "./LeadCaptureForm";
import { CloseTicketModal } from "./CloseTicketModal";
import { formatTicketAge, ticketAgeDays, ticketContactName, ticketNumberLabel } from "./supportAging";
import { useCrmHref } from "../../lib/customerUrl";
import { useLocale } from "../../shell/LocaleProvider";
import { ApiError } from "../../lib/api";
import { RecordUnavailable } from "./RecordUnavailable";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";

export function SupportTicketPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { ticketId } = useParams<{ ticketId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const crmHref = useCrmHref();
  const { formatDate, formatDateTime } = useLocale();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<(SupportTicket & { comments?: SupportComment[] }) | null>(null);
  useLoxleyOffer(ticket ? { kind: "ticket", id: ticket.id, label: ticket.title } : null);
  const [comment, setComment] = useState("");
  const [showLeadForm, setShowLeadForm] = useState(searchParams.get("qualify") === "1");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeComment, setCloseComment] = useState("");
  const [notifyUser, setNotifyUser] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const { can } = useAccess();

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      setTicket(await opsApi.getTicket(orgId, ticketId));
    } catch (err) {
      if (err instanceof ApiError && [403, 404, 410].includes(err.status)) {
        setUnavailable(true);
        return;
      }
      setError(err instanceof Error ? err.message : "Ticket could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [orgId, ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get("qualify") === "1") setShowLeadForm(true);
  }, [searchParams]);

  async function addComment() {
    if (!ticketId || !comment.trim()) return;
    setError(null);
    try {
      setTicket(await opsApi.patchTicket(orgId, ticketId, { comment: comment.trim() }));
      setComment("");
      reportSaved(show, "Comment saved.");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Comment could not be saved."));
    }
  }

  function startEditing() {
    if (!ticket) return;
    setDraftTitle(ticket.title);
    setDraftDescription(ticket.description ?? "");
    setEditing(true);
  }

  async function saveDetails() {
    if (!ticketId) return;
    setSaving(true);
    setError(null);
    try {
      setTicket(await opsApi.patchTicket(orgId, ticketId, { title: draftTitle.trim(), description: draftDescription.trim() }));
      setEditing(false);
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Ticket could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  async function confirmClose() {
    if (!ticketId || !closeComment.trim()) return;
    setClosing(true);
    setError(null);
    try {
      setTicket(
        await opsApi.patchTicket(orgId, ticketId, {
          status: "closed",
          comment: closeComment.trim(),
          notify_user: notifyUser,
        }),
      );
      setCloseOpen(false);
      setCloseComment("");
      setNotifyUser(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ticket could not be closed.");
    } finally {
      setClosing(false);
    }
  }

  async function submitLead(body: LeadInput) {
    if (!ticketId) throw new Error("Ticket could not be loaded.");
    const updated = await opsApi.generateLeadFromTicket(orgId, ticketId, {
      first_name: body.first_name ?? undefined,
      last_name: body.last_name ?? undefined,
      email: body.email ?? undefined,
      phone: body.phone ?? undefined,
      lead_score: body.lead_score,
      rating: body.rating ?? undefined,
      notes: body.notes ?? undefined,
      product_ids: body.product_ids,
    });
    setTicket(updated);
    if (!updated.generated_lead_id) throw new Error("Lead could not be generated.");
    return { id: updated.generated_lead_id, company_id: updated.company_id };
  }

  if (loading) return <Card>Loading...</Card>;
  if (unavailable) {
    return <RecordUnavailable objectLabel="support ticket" backTo={crmHref("/support")} backLabel="← Back to Support" />;
  }
  if (error && !ticket) return <Card className="crm-error">{error}</Card>;
  if (!ticket) return <Card>Ticket not found</Card>;

  const canEditDetails = ticket.status === "open" && can("crm.support", "update");
  const contactName = ticketContactName(ticket);
  const ageLabel = formatTicketAge(ticketAgeDays(ticket));

  return (
    <RecordWorkspace
      kind="support"
      objectLabel="Support ticket"
      title={ticket.title}
      subtitle={ticket.company_name}
      backTo={crmHref("/support")}
      backLabel="← Back to Support"
      highlights={[
        { label: "Status", value: <StatusBadge status={ticket.status} options={TICKET_STATUS_OPTIONS} /> },
        { label: "Opened", value: ticket.opened_at ? formatDate(ticket.opened_at) : "—" },
        { label: "Aging", value: ageLabel },
        { label: "Company", value: ticket.company_name },
        { label: "Contact", value: contactName || "—" },
        {
          label: "Lead",
          value: ticket.generated_lead_code ?? "Not generated",
        },
      ]}
      actions={
        <Cluster gap={2}>
          {ticket.generated_lead_id ? (
            <Button title="Open the CRM lead created from this ticket." onClick={() => navigate(crmHref(`/leads/${ticket.generated_lead_id}`))}>
              Open lead
            </Button>
          ) : (
            <Button
              title={ACTION_HINTS.generate_lead}
              onClick={() => {
                const next = !showLeadForm;
                setShowLeadForm(next);
                const params = new URLSearchParams(searchParams);
                if (next) params.set("qualify", "1");
                else params.delete("qualify");
                setSearchParams(params, { replace: true });
              }}
            >
              {showLeadForm ? "Cancel" : "Generate lead"}
            </Button>
          )}
          {ticket.status === "open" ? (
            <Button variant="secondary" title={ACTION_HINTS.close_ticket} onClick={() => setCloseOpen(true)}>
              Close ticket
            </Button>
          ) : null}
          <DeleteRecordButton
            capability="crm.support"
            recordLabel={ticketNumberLabel(ticket)}
            onDeleted={async () => {
              await opsApi.deleteTicket(orgId, ticket.id);
              navigate(crmHref("/support"));
            }}
          />
        </Cluster>
      }
      tabs={[{ key: "details", label: "Details" }]}
      activeTab="details"
      onTabChange={() => undefined}
    >
      {error && <p className="crm-error">{error}</p>}
      <DetailSection title="Why this ticket exists">
        <FieldItem label="Ticket Number" hint={FIELD_HINTS.ticket_number} value={ticketNumberLabel(ticket)} />
        <FieldItem
          label="Title"
          hint={FIELD_HINTS.ticket_title}
          value={
            editing ? (
              <Input aria-label="Title" value={draftTitle} maxLength={200} onChange={(event) => setDraftTitle(event.target.value)} />
            ) : (
              ticket.title
            )
          }
        />
        <FieldItem
          label="Issue description"
          hint={FIELD_HINTS.ticket_description}
          value={
            editing ? (
              <textarea
                aria-label="Issue description"
                className="crm-textarea"
                rows={4}
                maxLength={4000}
                value={draftDescription}
                onChange={(event) => setDraftDescription(event.target.value)}
              />
            ) : (
              ticket.description || "—"
            )
          }
        />
        {canEditDetails ? (
          <div className="crm-form-actions">
            {editing ? (
              <>
                <Button
                  title="Save the new title and description. Both are required."
                  onClick={() => saveDetails()}
                  disabled={saving || !draftTitle.trim() || !draftDescription.trim()}
                >
                  {saving ? "Saving…" : "Save changes"}
                </Button>
                <Button variant="secondary" title="Discard your changes." onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              </>
            ) : (
              <Button
                variant="secondary"
                title="Correct the title or issue description. Only open tickets can be edited; closed tickets must be reopened first."
                onClick={startEditing}
              >Edit title and description</Button>
            )}
          </div>
        ) : null}
        <FieldItem
          label="Company"
          hint={FIELD_HINTS.opportunity_company}
          value={
            ticket.company_id ? (
              <Link to={crmHref(`/companies/${ticket.company_id}`)}>{ticket.company_name}</Link>
            ) : (
              ticket.company_name
            )
          }
        />
        <FieldItem
          label="Contact"
          hint={FIELD_HINTS.contact_company}
          value={
            ticket.contact_id ? (
              <Link to={crmHref(`/companies/${ticket.company_id}`)}>{contactName || "Contact"}</Link>
            ) : (
              contactName || "—"
            )
          }
        />
        <FieldItem label="Status" hint={FIELD_HINTS.ticket_status} value={ticket.status} />
        <FieldItem label="Opened" hint="When this ticket was created." value={ticket.opened_at ? formatDateTime(ticket.opened_at) : "—"} />
        {ticket.closed_at ? (
          <FieldItem label="Closed" hint="When the ticket was marked closed." value={formatDateTime(ticket.closed_at)} />
        ) : null}
        <FieldItem label="Aging" hint={FIELD_HINTS.ticket_aging} value={ageLabel} />
      </DetailSection>
      {showLeadForm && !ticket.generated_lead_id ? (
        <div className="crm-panel-card">
          <h2 className="crm-section-title">New lead</h2>
          <LeadCaptureForm
            orgId={orgId}
            lockedCompany={{ id: ticket.company_id, name: ticket.company_name }}
            initial={{
              first_name: ticket.contact_first_name ?? "",
              last_name: ticket.contact_last_name ?? "",
              email: ticket.contact_email ?? "",
              phone: ticket.contact_phone ?? "",
              company_id: ticket.company_id,
              company_name: ticket.company_name,
              lead_source: "support",
              notes: [
                `Generated from support ticket ${ticketNumberLabel(ticket)}${ticket.title ? `: ${ticket.title}` : ""}`,
                ticket.description?.trim() || "",
              ]
                .filter(Boolean)
                .join("\n\n"),
            }}
            submitLabel="Create lead"
            submit={submitLead}
            onCreated={(res) => {
              setShowLeadForm(false);
              navigate(crmHref(`/leads/${res.id}`));
            }}
          />
        </div>
      ) : null}
      <div className="crm-panel-card">
        <h2 className="crm-section-title">Updates</h2>
        <Stack gap={3}>
          {(ticket.comments ?? []).length === 0 ? (
            <p className="crm-muted">No comments yet. Add a note about the work required or what was done.</p>
          ) : (
            (ticket.comments ?? []).map((item) => (
              <div key={item.id}>
                <div className="crm-muted">
                  {item.created_at ? formatDateTime(item.created_at) : "—"}
                  {item.author_name ? ` · ${item.author_name}` : ""}
                </div>
                <p>{item.body}</p>
              </div>
            ))
          )}
          {ticket.status === "open" ? (
            <>
              <div className="crm-resizable-field">
                <label className="crm-field" htmlFor="ticket-comment">Add a comment</label>
                <div className="crm-resizable-field-shell">
                  <textarea
                    id="ticket-comment"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder="What support is needed, or what was done?"
                  />
                </div>
              </div>
              <div className="crm-form-actions">
                <Button onClick={() => addComment()} disabled={!comment.trim()}>Save comment</Button>
              </div>
            </>
          ) : null}
        </Stack>
      </div>
      <CloseTicketModal
        open={closeOpen}
        comment={closeComment}
        notifyUser={notifyUser}
        canNotify={Boolean(ticket.contact_email)}
        onCommentChange={setCloseComment}
        onNotifyChange={setNotifyUser}
        onCancel={() => setCloseOpen(false)}
        onConfirm={() => confirmClose()}
        saving={closing}
      />
    </RecordWorkspace>
  );
}
